import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { FabiJobSearchQueryGenerationUseCase } from '@xing-com/crate-common-graphql-types';
import { useMatchMedia } from '@xing-com/crate-hooks-use-match-media';
import {
  IconAICareerChanger,
  IconAIProfileSearch,
  IconSalary,
} from '@xing-com/icons';
import { mediaConfined, mediaSmallOrTiny } from '@xing-com/layout-tokens';
import { Marker } from '@xing-com/marker';
import { spaceS, spaceXS, spaceXXS } from '@xing-com/tokens';
import { BodyCopy, Meta } from '@xing-com/typography';

import { ICON_SIZE, ICON_SMALL_SIZE } from './constants';

const CASES = {
  [FabiJobSearchQueryGenerationUseCase.EarnMore]: {
    title: 'JOBS_SEARCH_AI_SALARY_SUGGESTION',
    subtitle: 'JOBS_SEARCH_AI_SALARY_SUGGESTION_SUBTITLE',
    Icon: IconSalary,
    isNew: true,
  },
  [FabiJobSearchQueryGenerationUseCase.SearchWithProfile]: {
    title: 'JOBS_SEARCH_AI_SWIMP_SUGGESTION',
    subtitle: 'JOBS_SEARCH_AI_SWIMP_SUGGESTION_SUBTITLE',
    Icon: IconAIProfileSearch,
    isNew: false,
  },
  [FabiJobSearchQueryGenerationUseCase.SearchWithSkills]: {
    title: 'JOBS_SEARCH_AI_SWIMS_SUGGESTION',
    subtitle: 'JOBS_SEARCH_AI_SWIMS_SUGGESTION_SUBTITLE',
    Icon: IconAICareerChanger,
    isNew: false,
  },
};

type Props = {
  useCase: FabiJobSearchQueryGenerationUseCase;
};
export const InstantPrompt: React.FC<Props> = ({ useCase }) => {
  const isSmall = useMatchMedia(mediaSmallOrTiny);

  const { title, subtitle, Icon, isNew } = CASES[useCase];
  const iconSize = isSmall ? ICON_SMALL_SIZE : ICON_SIZE;

  return (
    <Container>
      <StyledIcon as={Icon} width={iconSize} height={iconSize} />
      <SuggestionText>
        <SuggestionTitle>
          <SuggestionBodyCopy noMargin lineHeight="dense">
            <FormattedMessage id={title} />
          </SuggestionBodyCopy>
          {isNew ? (
            <Marker variant="ai" aria-hidden="true">
              <FormattedMessage id="XDS_FLAG_NEW_NEUTRAL" />
            </Marker>
          ) : null}
        </SuggestionTitle>
        {subtitle && !isSmall ? (
          <SuggestionSubtitle size="small" noMargin>
            <FormattedMessage id={subtitle} />
          </SuggestionSubtitle>
        ) : null}
      </SuggestionText>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  gap: ${spaceS};
  margin-block: -${spaceXS};

  @media ${mediaConfined} {
    gap: 10px;
    margin-block: 0;
  }
`;

const StyledIcon = styled.div`
  flex-shrink: 0;
`;

const SuggestionText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  gap: ${spaceXXS};
`;

const SuggestionTitle = styled.div`
  display: flex;
  gap: ${spaceXS};
  align-items: start;
  width: 100%;

  @media ${mediaConfined} {
    align-items: center;
  }
`;

const SuggestionBodyCopy = styled(BodyCopy)`
  text-wrap: balance;
`;

const SuggestionSubtitle = styled(Meta)`
  text-wrap: balance;
`;
